import { type DayConfig, dayConfigs } from '@/config/DayConfig';
import { npcConfigs } from '@/config/NPCConfig';
import type { Player } from '@/entities';
import { NPC } from '@/entities';
import { SoundManager } from '@/systems/SoundManager';
import type { DialogChoice, DialogTree } from '@/types';
import {getItemConfig, ItemConfig} from "@/config/ItemsConfig.ts";
import {Game} from "@/core";

// Interface for tracking NPC interaction states
export interface NPCInteractionState {
  hasInteracted: boolean;
  lastDialogNodeId?: string;
  lastSpeaker?: string;
  lastText?: string;
  lastCharacterImage?: string;
  lastBackgroundImage?: string;
  lastIsEnd?: boolean;
  lastChoices?: Array<{
    text: string;
    wasSelected: boolean;
  }>;
  interactionCount: number;
  firstInteractionTime?: number;
  lastInteractionTime?: number;
  isRepeatable?: boolean; // Flag to indicate if this NPC should have repeatable dialogs
}

export class StoryManager {

  private static instance: StoryManager;
  private affection: number = 0;
  private hostility: number = 0;
  private eventListeners: Map<string, ((value: number) => void)[]> = new Map();
  private player: Player | null = null; // Reference to the player object

  private gameStates:string[] = []
  private customValues:any = {}

  // Track NPC interaction states
  private npcInteractionStates: Map<string, NPCInteractionState> = new Map();

  // Track special dialogs unlocked (for winning condition)
  private unlockedSpecialDialogs: Set<string> = new Set();

  // Track NPCs with repeatable dialogs (populated from NPC configs)
  private repeatableNPCs: Set<string> = new Set();

  // Manage NPCs and day system
  private npcs: NPC[] = [];
  private currentDay: number = 1;
  private dayEventListeners: Map<string, ((day: number) => void)[]> = new Map();

  // Track whether we've shown the initial dialog for the current day
  private dayDialogShown: Set<number> = new Set();

  // Inventory system - Map of itemId to quantity
  private inventory: Map<string, number> = new Map();
  private inventoryEventListeners: Map<string, ((item?:ItemConfig) => void)[]> = new Map();

  // Global Discovered Items
  private discoveredItems: Set<string> = new Set();
  private readonly DISCOVERED_ITEMS_KEY = 'discovered_items';

  private constructor() {
    console.log(
      `StoryManager initialized. Starting affection: ${this.affection}, hostility: ${this.hostility}`,
    );
    this.loadDiscoveredItems();
  }

  public static getInstance(): StoryManager {
    if (!StoryManager.instance) {
      StoryManager.instance = new StoryManager();
    }
    return StoryManager.instance;
  }

  public addGameState(state:string){
    this.gameStates.push(state)
    console.log("GAMESTATES", this.gameStates)
    return
  }

  public checkStateExists(state:string):boolean{
    return this.gameStates.includes(state);
  }

  public removeGameState(state:string){
    //TODO add remove game state
  }

  public checkCustomValue(key:string):number{
    return this.customValues[key] ? this.customValues[key] : 0
  }

  public addCustomValue(key:string, value:number){
    if(this.customValues[key]){
      this.customValues[key] = this.customValues[key] + value
    }else{
      this.customValues[key] = value
    }

    if(this.customValues["general_karma"] < 0){
      Game.getInstance().setVignetteDisplayBPM(3)
    }

    if(this.customValues["general_karma"] < -1){
      Game.getInstance().setVignetteDisplayBPM(5)
    }

    if(this.customValues["general_karma"] < -2){
      Game.getInstance().setVignetteDisplayBPM(7)
    }

    if(this.customValues["false_arguments"] > 0){
      Game.getInstance().setVignetteDisplayBPM(10)
    }

    if(this.customValues["false_arguments"] > 1){
      Game.getInstance().setVignetteDisplayBPM(25)
    }

    if(this.customValues["false_arguments"] > 2){
      Game.getInstance().setVignetteDisplayBPM(70)
    }

  }

  public addPoints(amount: number): void {
    if (amount > 0) {
      this.affection += amount;
      console.log(`Affection added: +${amount}. Total affection: ${this.affection}`);
      this.notifyListeners('affectionChanged', this.affection);
      this.notifyListeners('affectionAdded', amount);
    } else if (amount < 0) {
      this.hostility += Math.abs(amount);
      console.log(`Hostility added: +${Math.abs(amount)}. Total hostility: ${this.hostility}`);
      this.notifyListeners('hostilityChanged', this.hostility);
      this.notifyListeners('hostilityAdded', Math.abs(amount));
    }
    // For backward compatibility, also notify about total points change
    this.notifyListeners('pointsChanged', this.getTotalPoints());
    this.notifyListeners('pointsAdded', amount);
  }

  public deductPoints(amount: number): void {
    if (amount > 0) {
      this.hostility += amount;
      console.log(`Hostility added: +${amount}. Total hostility: ${this.hostility}`);
      this.notifyListeners('hostilityChanged', this.hostility);
      this.notifyListeners('hostilityAdded', amount);
    } else if (amount < 0) {
      const affectionToDeduct = Math.abs(amount);
      this.affection = Math.max(0, this.affection - affectionToDeduct);
      console.log(`Affection deducted: -${affectionToDeduct}. Total affection: ${this.affection}`);
      this.notifyListeners('affectionChanged', this.affection);
      this.notifyListeners('affectionDeducted', affectionToDeduct);
    }
    // For backward compatibility, also notify about total points change
    this.notifyListeners('pointsChanged', this.getTotalPoints());
    this.notifyListeners('pointsDeducted', amount);
  }

  // New methods for getting individual values
  public getAffection(): number {
    return this.affection;
  }

  public getHostility(): number {
    return this.hostility;
  }

  // Updated method for backward compatibility
  public getPoints(): number {
    return this.getTotalPoints();
  }

  // Calculate total points as affection minus hostility
  public getTotalPoints(): number {
    return this.affection - this.hostility;
  }

  public setPoints(amount: number): void {
    // Reset both values and distribute the amount
    this.affection = Math.max(0, amount);
    this.hostility = Math.max(0, -amount);
    console.log(
      `Points set to: ${amount}. Affection: ${this.affection}, Hostility: ${this.hostility}`,
    );
    this.notifyListeners('affectionChanged', this.affection);
    this.notifyListeners('hostilityChanged', this.hostility);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
  }

  // New methods for setting individual values
  public setAffection(amount: number): void {
    this.affection = Math.max(0, amount);
    console.log(`Affection set to: ${this.affection}`);
    this.notifyListeners('affectionChanged', this.affection);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
  }

  public setHostility(amount: number): void {
    this.hostility = Math.max(0, amount);
    console.log(`Hostility set to: ${this.hostility}`);
    this.notifyListeners('hostilityChanged', this.hostility);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
  }

  // Event system for UI updates
  public addEventListener(event: string, callback: (value: number) => void): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  public removeEventListener(event: string, callback: (value: number) => void): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyListeners(event: string, value: number): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback(value);
      });
    }
  }

  // Story milestone checks
  public hasReachedMilestone(threshold: number): boolean {
    return this.getTotalPoints() >= threshold;
  }

  public isInRange(min: number, max: number): boolean {
    const totalPoints = this.getTotalPoints();
    return totalPoints >= min && totalPoints <= max;
  }

  // New milestone checks for individual values
  public hasReachedAffectionMilestone(threshold: number): boolean {
    return this.affection >= threshold;
  }

  public hasReachedHostilityMilestone(threshold: number): boolean {
    return this.hostility >= threshold;
  }

  public isAffectionInRange(min: number, max: number): boolean {
    return this.affection >= min && this.affection <= max;
  }

  public isHostilityInRange(min: number, max: number): boolean {
    return this.hostility >= min && this.hostility <= max;
  }

  // Special dialog management for winning condition
  public checkChoiceRequirements(choice: DialogChoice): boolean {
    if (!choice.requirements) return true;

    const req = choice.requirements;

    // Check affection requirements
    if (req.minAffection !== undefined && this.affection < req.minAffection) return false;
    if (req.maxAffection !== undefined && this.affection > req.maxAffection) return false;

    // Check hostility requirements
    if (req.minHostility !== undefined && this.hostility < req.minHostility) return false;
    if (req.maxHostility !== undefined && this.hostility > req.maxHostility) return false;

    // Check total points requirements
    const totalPoints = this.getTotalPoints();
    if (req.minTotalPoints !== undefined && totalPoints < req.minTotalPoints) return false;
    if (req.maxTotalPoints !== undefined && totalPoints > req.maxTotalPoints) return false;

    // Check item requirements
    if (req.requiredItems) {
      for (const itemReq of req.requiredItems) {
        if (!this.hasItem(itemReq.itemId, itemReq.quantity)) {
          return false;
        }
      }
    }

    // Check state requirements
    if (req.requiredStates) {
      for (const state of req.requiredStates) {
        if (!this.checkStateExists(state)) {
          return false;
        }
      }
    }

    if (req.hideOnstates) {
      for (const state of req.hideOnstates) {
        if (this.checkStateExists(state)) {
          return false;
        }
      }
    }

    if (req.maxRequiredValue) {
      for (const param of req.maxRequiredValue) {
        if (this.checkCustomValue(param.key) > param.value) {
          return false;
        }
      }
    }

    if (req.minRequiredValue) {
      for (const param of req.minRequiredValue) {
        if (this.checkCustomValue(param.key) < param.value) {
          return false;
        }
      }
    }

    return true;
  }

  public unlockSpecialDialog(dialogId: string, npcName: string, specialType?: string): void {
    const key = `${npcName}:${dialogId}`;
    this.unlockedSpecialDialogs.add(key);
    console.log(`Special dialog unlocked: ${key} (${specialType || 'unknown type'})`);

    // Notify listeners about special dialog unlock
    this.notifyListeners('specialDialogUnlocked', this.unlockedSpecialDialogs.size);
  }

  public hasUnlockedSpecialDialog(dialogId: string, npcName: string): boolean {
    const key = `${npcName}:${dialogId}`;
    return this.unlockedSpecialDialogs.has(key);
  }

  public getUnlockedSpecialDialogs(): string[] {
    return Array.from(this.unlockedSpecialDialogs);
  }

  public getSpecialDialogCount(): number {
    return this.unlockedSpecialDialogs.size;
  }

  // Inventory management methods
  public addItem(itemId: string, quantity: number = 1): void {
    const currentQuantity = this.inventory.get(itemId) || 0;
    const newQuantity = currentQuantity + quantity;
    this.inventory.set(itemId, newQuantity);
    
    // Mark as discovered globally
    if (!this.discoveredItems.has(itemId)) {
        this.discoveredItems.add(itemId);
        this.saveDiscoveredItems();
        console.log(`New item discovered: ${itemId}`);
    }

    let itemConfig = getItemConfig(itemId)
    console.log(`Added ${quantity}x ${itemId}. Total: ${newQuantity}`);
    this.notifyInventoryListeners('inventoryChanged');
    this.notifyInventoryListeners('showInventory', itemConfig);
  }

  public removeItem(itemId: string, quantity: number = 1): boolean {
    const currentQuantity = this.inventory.get(itemId) || 0;
    if (currentQuantity < quantity) {
      console.warn(`Cannot remove ${quantity}x ${itemId}. Only have ${currentQuantity}`);
      return false;
    }
    const newQuantity = currentQuantity - quantity;
    if (newQuantity === 0) {
      this.inventory.delete(itemId);
    } else {
      this.inventory.set(itemId, newQuantity);
    }
    console.log(`Removed ${quantity}x ${itemId}. Remaining: ${newQuantity}`);
    this.notifyInventoryListeners('inventoryChanged');
    return true;
  }

  public hasItem(itemId: string, minQuantity: number = 1): boolean {
    const currentQuantity = this.inventory.get(itemId) || 0;
    return currentQuantity >= minQuantity;
  }

  public getItemQuantity(itemId: string): number {
    return this.inventory.get(itemId) || 0;
  }

  public getInventory(): Map<string, number> {
    return new Map(this.inventory);
  }

  public clearInventory(): void {
    this.inventory.clear();
    console.log('Inventory cleared');
    this.notifyInventoryListeners('inventoryChanged');
  }

  // Inventory event system
  public addInventoryEventListener(event: string, callback: () => void): void {
    if (!this.inventoryEventListeners.has(event)) {
      this.inventoryEventListeners.set(event, []);
    }
    const listeners = this.inventoryEventListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  public removeInventoryEventListener(event: string, callback: () => void): void {
    const listeners = this.inventoryEventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyInventoryListeners(event: string, itemConfig?:ItemConfig): void {
    const listeners = this.inventoryEventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback(itemConfig);
      });
    }
  }

  // Discovered Items Management
  private loadDiscoveredItems(): void {
    try {
        const saved = localStorage.getItem(this.DISCOVERED_ITEMS_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (Array.isArray(parsed)) {
                this.discoveredItems = new Set(parsed);
                console.log(`Loaded ${this.discoveredItems.size} discovered items from localStorage.`);
            }
        }
    } catch (e) {
        console.error('Failed to load discovered items:', e);
    }
  }

  private saveDiscoveredItems(): void {
    try {
        const array = Array.from(this.discoveredItems);
        localStorage.setItem(this.DISCOVERED_ITEMS_KEY, JSON.stringify(array));
    } catch (e) {
        console.error('Failed to save discovered items:', e);
    }
  }

  public getDiscoveredItems(): Set<string> {
      return new Set(this.discoveredItems);
  }

  public isItemDiscovered(itemId: string): boolean {
      return this.discoveredItems.has(itemId);
  }

  // NPC interaction tracking methods
  public markNPCInteraction(
    npcName: string,
    dialogNodeId: string,
    speaker: string,
    text: string,
    characterImage?: string,
    backgroundImage?: string,
    isEnd?: boolean,
    choices?: Array<{ text: string; wasSelected: boolean }>,
  ): void {
    const currentTime = Date.now();
    const isRepeatable = this.repeatableNPCs.has(npcName);
    const existing = this.npcInteractionStates.get(npcName);

    if (existing) {
      // Update existing interaction
      existing.lastDialogNodeId = dialogNodeId;
      existing.lastSpeaker = speaker;
      existing.lastText = text;
      existing.lastCharacterImage = characterImage;
      existing.lastBackgroundImage = backgroundImage;
      existing.lastIsEnd = isEnd;
      existing.lastChoices = choices;
      existing.interactionCount++;
      existing.lastInteractionTime = currentTime;
      existing.isRepeatable = isRepeatable;
    } else {
      // Create new interaction state
      this.npcInteractionStates.set(npcName, {
        hasInteracted: true,
        lastDialogNodeId: dialogNodeId,
        lastSpeaker: speaker,
        lastText: text,
        lastCharacterImage: characterImage,
        lastBackgroundImage: backgroundImage,
        lastIsEnd: isEnd,
        lastChoices: choices,
        interactionCount: 1,
        firstInteractionTime: currentTime,
        lastInteractionTime: currentTime,
        isRepeatable: isRepeatable,
      });
    }

    console.log(
      `NPC interaction recorded: ${npcName} - ${dialogNodeId}${isRepeatable ? ' (repeatable)' : ''}`,
    );
  }

  public hasInteractedWithNPC(npcName: string): boolean {
    const state = this.npcInteractionStates.get(npcName);
    const isRepeatable = this.repeatableNPCs.has(npcName);

    // For repeatable NPCs, always return false so they can be interacted with again
    if (isRepeatable) {
      return false;
    }

    return state?.hasInteracted ?? false;
  }

  public getNPCInteractionState(npcName: string): NPCInteractionState | null {
    return this.npcInteractionStates.get(npcName) ?? null;
  }

  public getAllNPCInteractionStates(): Map<string, NPCInteractionState> {
    return new Map(this.npcInteractionStates);
  }

  public resetNPCInteraction(npcName: string): void {
    this.npcInteractionStates.delete(npcName);
    console.log(`NPC interaction reset: ${npcName}`);
  }

  public resetAllNPCInteractions(): void {
    this.npcInteractionStates.clear();
    console.log('All NPC interactions reset');
  }

  public resetNonRepeatableNPCInteractions(): void {
    const keysToDelete: string[] = [];

    for (const [npcName] of this.npcInteractionStates.entries()) {
      if (!this.repeatableNPCs.has(npcName)) {
        keysToDelete.push(npcName);
      }
    }

    for (const key of keysToDelete) {
      this.npcInteractionStates.delete(key);
    }

    console.log(`Reset interactions for non-repeatable NPCs: ${keysToDelete.join(', ')}`);
  }

  public getLastDialogNode(
    npcName: string,
  ): { speaker: string; text: string; nodeId: string } | null {
    const state = this.npcInteractionStates.get(npcName);
    const isRepeatable = this.repeatableNPCs.has(npcName);

    // For repeatable NPCs, don't return last dialog to force fresh interactions
    if (isRepeatable) {
      return null;
    }

    if (!state || !state.lastDialogNodeId || !state.lastSpeaker || !state.lastText) {
      return null;
    }

    return {
      speaker: state.lastSpeaker,
      text: state.lastText,
      nodeId: state.lastDialogNodeId,
    };
  }

  // Methods to manage repeatable NPCs
  public addRepeatableNPC(npcName: string): void {
    this.repeatableNPCs.add(npcName);
    console.log(`Added ${npcName} to repeatable NPCs list`);
  }

  public removeRepeatableNPC(npcName: string): void {
    this.repeatableNPCs.delete(npcName);
    console.log(`Removed ${npcName} from repeatable NPCs list`);
  }

  public isRepeatableNPC(npcName: string): boolean {
    return this.repeatableNPCs.has(npcName);
  }

  public getRepeatableNPCs(): string[] {
    return Array.from(this.repeatableNPCs);
  }

  public setRepeatableNPCs(npcNames: string[]): void {
    this.repeatableNPCs = new Set(npcNames);
    console.log(`Set repeatable NPCs: ${npcNames.join(', ')}`);
  }

  // Save/load functionality for future use
  public getState(): {
    points: number;
    affection: number;
    hostility: number;
    currentDay: number;
    npcInteractionStates: [string, NPCInteractionState][];
    unlockedSpecialDialogs: string[];
    repeatableNPCs: string[];
    dayDialogShown: number[];
    inventory: [string, number][];
  } {
    return {
      points: this.getTotalPoints(), // For backward compatibility
      affection: this.affection,
      hostility: this.hostility,
      currentDay: this.currentDay,
      npcInteractionStates: Array.from(this.npcInteractionStates.entries()),
      unlockedSpecialDialogs: Array.from(this.unlockedSpecialDialogs),
      repeatableNPCs: Array.from(this.repeatableNPCs),
      dayDialogShown: Array.from(this.dayDialogShown),
      inventory: Array.from(this.inventory.entries()),
    };
  }

  public loadState(state: {
    points?: number;
    affection?: number;
    hostility?: number;
    currentDay?: number;
    npcInteractionStates?: [string, NPCInteractionState][];
    unlockedSpecialDialogs?: string[];
    repeatableNPCs?: string[];
    dayDialogShown?: number[];
    inventory?: [string, number][];
  }): void {
    // Load affection and hostility if available
    if (state.affection !== undefined) {
      this.affection = state.affection;
    }
    if (state.hostility !== undefined) {
      this.hostility = state.hostility;
    }

    // Load unlocked special dialogs
    if (state.unlockedSpecialDialogs) {
      this.unlockedSpecialDialogs = new Set(state.unlockedSpecialDialogs);
    }

    // Load repeatable NPCs if available
    if (state.repeatableNPCs) {
      this.repeatableNPCs = new Set(state.repeatableNPCs);
    }

    // Load day dialog shown flags if available
    if (state.dayDialogShown) {
      this.dayDialogShown = new Set(state.dayDialogShown);
    }

    // Load inventory if available
    if (state.inventory) {
      this.inventory = new Map(state.inventory);
      console.log(`Inventory loaded with ${this.inventory.size} item types`);
    }

    // Fallback to old points system for backward compatibility
    if (
      state.points !== undefined &&
      state.affection === undefined &&
      state.hostility === undefined
    ) {
      this.setPoints(state.points);
    }

    console.log(
      `State loaded - Affection: ${this.affection}, Hostility: ${this.hostility}, Total: ${this.getTotalPoints()}`,
    );

    // Load current day if available
    if (
      state.currentDay !== undefined &&
      state.currentDay >= 1 &&
      state.currentDay <= dayConfigs.length
    ) {
      this.currentDay = state.currentDay;
    }

    // Load NPC interaction states if available (but they will be reset when day is set)
    if (state.npcInteractionStates) {
      this.npcInteractionStates = new Map(state.npcInteractionStates);
    }

    // Reinitialize NPCs for the loaded day and reset interactions for non-repeatable NPCs
    this.initializeNPCs();
    this.resetNonRepeatableNPCInteractions();

    this.notifyListeners('affectionChanged', this.affection);
    this.notifyListeners('hostilityChanged', this.hostility);
    this.notifyListeners('pointsChanged', this.getTotalPoints());
    this.notifyDayListeners('dayChanged', this.currentDay);
  }

  // Day system methods
  public getCurrentDay(): number {
    return this.currentDay;
  }

  public getCurrentDayConfig(): DayConfig | null {
    return dayConfigs.find(config => config.day === this.currentDay) || null;
  }

  public setDay(day: number): void {
    if (day < 1 || day > dayConfigs.length) {
      console.warn(`Invalid day: ${day}. Must be between 1 and ${dayConfigs.length}`);
      return;
    }

    const oldDay = this.currentDay;
    this.currentDay = day;

    // Reinitialize NPCs with the new day's configuration
    this.initializeNPCs();

    // Clear interaction states when changing days for non-repeatable NPCs only
    // This allows re-interaction with regular NPCs but preserves repeatable NPC state
    this.resetNonRepeatableNPCInteractions();

    // Check if there's a teleport position defined for this day and teleport the player if available
    const dayConfig = this.getCurrentDayConfig();
    if (dayConfig?.position && this.player) {
      console.log(
        `Teleporting player to position: x=${dayConfig.position.x}, y=${dayConfig.position.y}`,
      );
      this.player.setPosition(dayConfig.position.x, dayConfig.position.y);
    }
    
    // Set player facing direction if configured
    if (dayConfig?.facing && this.player) {
      console.log(`Setting player facing direction to: ${dayConfig.facing}`);
      this.player.setDirection(dayConfig.facing);
    }

    console.log(`Day changed from ${oldDay} to ${day}`);

    // Play day-specific music if configured
    if (dayConfig?.music) {
      const soundManager = SoundManager.getInstance();
      // Add 'music_' prefix to music ID when playing
      soundManager.playMusic(`music_${dayConfig.music}`);
      console.log(`Playing day music: ${dayConfig.music}`);
    } else {
      console.log('No music configured for this day');
    }

    this.notifyDayListeners('dayChanged', day);
  }

  public nextDay(howManyDays?:number): boolean {
    if (this.currentDay < dayConfigs.length) {
      this.setDay(this.currentDay + (howManyDays ? howManyDays : 1));
      return true;
    }
    return false;
  }

  public previousDay(): boolean {
    if (this.currentDay > 1) {
      this.setDay(this.currentDay - 1);
      return true;
    }
    return false;
  }

  public getTotalDays(): number {
    return dayConfigs.length;
  }

  public getDayInfo(day?: number): DayConfig | null {
    const targetDay = day ?? this.currentDay;
    return dayConfigs.find(config => config.day === targetDay) || null;
  }

  public getAllDayConfigs(): DayConfig[] {
    return [...dayConfigs];
  }

  // Initial day dialog management
  public shouldShowInitialDayDialog(day?: number): boolean {
    const targetDay = day ?? this.currentDay;
    const hasShown = this.dayDialogShown.has(targetDay);
    console.log(
      `shouldShowInitialDayDialog for day ${targetDay}: hasShown=${hasShown}, result=${!hasShown}`,
    );
    return !hasShown;
  }

  public getInitialDayDialog(day?: number): DialogTree | null {
    const targetDay = day ?? this.currentDay;
    const dayConfig = dayConfigs.find(config => config.day === targetDay);
    const dialog = dayConfig?.initialDialog || null;
    console.log(
      `getInitialDayDialog for day ${targetDay}: found config=${!!dayConfig}, has dialog=${!!dialog}`,
    );
    return dialog;
  }

  public markInitialDayDialogShown(day?: number): void {
    const targetDay = day ?? this.currentDay;
    this.dayDialogShown.add(targetDay);
    console.log(`Initial dialog marked as shown for day ${targetDay}`);
  }

  public resetInitialDayDialogFlags(): void {
    this.dayDialogShown.clear();
    console.log('All initial day dialog flags reset');
  }

  // Day event system
  public addDayEventListener(event: string, callback: (day: number) => void): void {
    if (!this.dayEventListeners.has(event)) {
      this.dayEventListeners.set(event, []);
    }
    const listeners = this.dayEventListeners.get(event);
    if (listeners) {
      listeners.push(callback);
    }
  }

  public removeDayEventListener(event: string, callback: (day: number) => void): void {
    const listeners = this.dayEventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private notifyDayListeners(event: string, day: number): void {
    const listeners = this.dayEventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        callback(day);
      });
    }
  }

  // NPC Management Methods (updated to use current day)
  public initializeNPCs(): NPC[] {
    // Clear existing NPCs array
    this.npcs = [];

    // Get current day configuration
    const currentDayConfig = this.getCurrentDayConfig();
    if (!currentDayConfig) {
      console.warn(`No configuration found for day ${this.currentDay}, using day 1 as fallback`);
      // Fallback to day 1 or use legacy npcConfigs
      const fallbackConfig = dayConfigs[0] || { day: 1, name: 'Default', npcs: npcConfigs };
      for (const npcConfig of fallbackConfig.npcs) {
        this.npcs.push(new NPC(npcConfig));
      }
    } else {
      // Create NPCs from current day configuration
      for (const npcConfig of currentDayConfig.npcs) {
        this.npcs.push(new NPC(npcConfig));
      }
    }

    console.log(`Initialized ${this.npcs.length} NPCs for day ${this.currentDay}`);
    return this.npcs;
  }

  public getNPCs(): NPC[] {
    return this.npcs;
  }

  // Methods to manage the player reference
  public setPlayer(player: Player): void {
    this.player = player;
    console.log('Player reference set in StoryManager');
  }

  public getPlayer(): Player | null {
    return this.player;
  }

  public updateNPCs(
    deltaTime: number,
    playerPosition: { x: number; y: number },
  ): {
    playerNearNPC: boolean;
    closestNPC: NPC | null;
  } {
    let playerNearNPC = false;
    let closestNPC: NPC | null = null;
    let closestDistance = Infinity;

    // Get player collider for accurate triggerOnCollision detection
    const playerCollider = this.player?.getCollider();

    // First pass: update all NPCs and find closest
    for (const npc of this.npcs) {
      npc.update(deltaTime, playerPosition, playerCollider);

      if (npc.canInteract(playerPosition)) {
        playerNearNPC = true;

        // Calculate distance to find closest NPC
        const dx = playerPosition.x - npc.getPosition().x;
        const dy = playerPosition.y - npc.getPosition().y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestNPC = npc;
        }
      }
    }

    // Second pass: set focus only on the closest NPC
    for (const npc of this.npcs) {
      npc.setFocused(npc === closestNPC);
    }

    return { playerNearNPC, closestNPC };
  }

  public handleNPCInteraction(playerPosition: { x: number; y: number }):
    | DialogTree
    | {
        type: 'repeat';
        speaker: string;
        text: string;
        characterImage?: string;
        backgroundImage?: string;
        isEnd?: boolean;
      }
    | null {
    // Check if player is near any focused NPC
    for (const npc of this.npcs) {
      if (npc.canInteract(playerPosition) && npc.getFocused()) {
        return npc.interact();
      }
    }
    return null;
  }

  public setNPCCollidersVisible(visible: boolean): void {
    for (const npc of this.npcs) {
      npc.setColliderVisible(visible);
    }
  }
}
